import { z } from 'zod';
import {
  petChassisFinishWireSchema,
  petEnergyColorWireSchema,
  petUnlockKindSchema,
} from '../inventory/pet-appearance';
import { petTrainingSchema } from '../inventory/pet-consumables';
import { activePetSchema, petsResponseSchema } from './pets';

/**
 * Feeding a pet, unlocking a palette variant, and wearing one.
 *
 * Three separate operations on purpose, because they are three different
 * kinds of thing:
 *
 * - **Feeding** spends a consumable and changes the pet permanently.
 * - **Unlocking** spends a consumable and changes the ACCOUNT permanently.
 * - **Applying** spends nothing and is repeatable; unlocking was the purchase,
 *   and wearing what you own is not a second one.
 *
 * All three carry an idempotency key, and none of them accepts a value the
 * server could not have derived itself: a request names an instance and an id,
 * never a bonus, a percentage, a colour or a quantity.
 */

export const PET_FEED_ROUTE = '/api/pets/feed' as const;
export const PET_UNLOCK_ROUTE = '/api/pets/unlock' as const;
export const PET_APPEARANCE_ROUTE = '/api/pets/appearance' as const;
export const PET_PALETTE_ROUTE = '/api/pets/palette' as const;

/* -------------------------------------------------------------------------- */
/*  The palette an account can choose from                                    */
/* -------------------------------------------------------------------------- */

/**
 * Every colour and finish, with whether THIS account has it.
 *
 * The whole catalog travels, locked entries included: a locked option is
 * information - it is how a player learns Void Purple exists and which item
 * unlocks it - and hiding it would make the palette look complete when it is
 * not. `stock` says how many of the unlocking item they already hold, so the
 * button can say "unlock" rather than "go and find one" when they can.
 */
export const petPaletteEntrySchema = z.object({
  unlockItemId: z.string().min(1),
  /** Units of the unlocking consumable the account holds, as a string. */
  stock: z.string().regex(/^\d+$/),
});
export type PetPaletteEntry = z.infer<typeof petPaletteEntrySchema>;

export const petPaletteResponseSchema = z.object({
  energies: z.array(petEnergyColorWireSchema.extend({ stock: z.string().regex(/^\d+$/) })),
  finishes: z.array(petChassisFinishWireSchema.extend({ stock: z.string().regex(/^\d+$/) })),
  /** Units of each food the account holds, as decimal strings. */
  damageFoodStock: z.string().regex(/^\d+$/),
  attackSpeedFoodStock: z.string().regex(/^\d+$/),
});
export type PetPaletteResponse = z.infer<typeof petPaletteResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Feeding                                                                   */
/* -------------------------------------------------------------------------- */

export const petFeedRequestSchema = z
  .object({
    petInstanceId: z.uuid(),
    /** Which food. The server decides what it does; the client only names it. */
    foodDefinitionId: z.string().min(1),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type PetFeedRequest = z.infer<typeof petFeedRequestSchema>;

export const petFeedResponseSchema = z.object({
  petInstanceId: z.uuid(),
  /** The counters AFTER the feed, and the bonus derived from them. */
  training: petTrainingSchema,
  /** Units of that food left. */
  remaining: z.string().regex(/^\d+$/),
  /** The whole pet list again, so the panel never guesses. */
  pets: petsResponseSchema,
  /** The active pet, if this feed changed it. */
  activePet: activePetSchema.nullable(),
  replayed: z.boolean(),
});
export type PetFeedResponse = z.infer<typeof petFeedResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Unlocking                                                                 */
/* -------------------------------------------------------------------------- */

export const petUnlockRequestSchema = z
  .object({
    /** The consumable to spend: an Energy Sphere or a Tech Paint Bucket. */
    unlockDefinitionId: z.string().min(1),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type PetUnlockRequest = z.infer<typeof petUnlockRequestSchema>;

export const petUnlockResponseSchema = z.object({
  kind: petUnlockKindSchema,
  unlockId: z.string().min(1),
  /**
   * True when the variant was ALREADY unlocked.
   *
   * A normal outcome, not an error: the player pressed a button on something
   * they own. `consumed` is then false and the item is still theirs.
   */
  alreadyUnlocked: z.boolean(),
  consumed: z.boolean(),
  /** Units left, or null when nothing was spent. */
  remaining: z.string().regex(/^\d+$/).nullable(),
  palette: petPaletteResponseSchema,
  replayed: z.boolean(),
});
export type PetUnlockResponse = z.infer<typeof petUnlockResponseSchema>;

/* -------------------------------------------------------------------------- */
/*  Applying                                                                  */
/* -------------------------------------------------------------------------- */

export const petAppearanceRequestSchema = z
  .object({
    petInstanceId: z.uuid(),
    /** Both are catalog ids, validated server-side against the unlocks. */
    energyId: z.string().min(1),
    finishId: z.string().min(1),
    idempotencyKey: z.string().min(8).max(128),
  })
  .strict();
export type PetAppearanceRequest = z.infer<typeof petAppearanceRequestSchema>;

export const petAppearanceResponseSchema = z.object({
  petInstanceId: z.uuid(),
  energyId: z.string().min(1),
  finishId: z.string().min(1),
  pets: petsResponseSchema,
  activePet: activePetSchema.nullable(),
  replayed: z.boolean(),
});
export type PetAppearanceResponse = z.infer<typeof petAppearanceResponseSchema>;
